import Box from '@mui/material/Box'
import Icon from '@mui/material/Icon'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import { History } from 'history'
import { path } from 'ramda'
import React from 'react'

import SearchBar from '@/app/component/atom/search'
import TablePagination from '@/app/component/atom/table-pagination'
import Tooltip from '@/app/component/atom/tooltip'
import Loading from '@/app/component/guard/loading'
import ContentLayout from '@/app/component/layout/content'
import TableToolbar from '@/app/component/layout/table/toolbar'
import { getOrgSubs as getOrgSubsAction, navigateOrgSubs, setCATISubsPageSize } from '@/app/module/cati/store/actions'
import { OrgSubscriptionTableType } from '@/app/module/cati/types'
import { SortOrder } from '@/app/types'
import config from '@/config'

import ContactsSettingsMenu from './contacts-settings-menu'
import SubCard from './sub-card'

const pageSizeDefault = path(['modules', 'cati', 'pageSize', 'default'], config) as number

const PAGE_SIZE_VALUES = [
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '30', value: 30 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
]

type Props = {
  callContactMode: boolean
  history: History
  isCallContactModeSaving?: boolean
  loading: boolean
  orgId: number
  page: number
  pageSearchTerm?: string | null
  searchTerm: string | null
  size: number
  timezone: string
  token: string
  total: number
  userId: number
  values: OrgSubscriptionTableType[]
  getOrgSubs: typeof getOrgSubsAction
  navigate: typeof navigateOrgSubs
  onCallContactModeChange: (enabled: boolean) => void
  setPageSize: typeof setCATISubsPageSize
}

const MobileSubs: React.FC<Props> = (props) => {
  const {
    values,
    total,
    searchTerm,
    size = pageSizeDefault,
    page,
    navigate,
    history,
    setPageSize,
    orgId,
    token,
    getOrgSubs,
    timezone,
    loading,
    callContactMode,
    isCallContactModeSaving,
    onCallContactModeChange,
  } = props

  const getSubs = React.useCallback(() => {
    if (!orgId || !token) {
      return
    }

    getOrgSubs({
      token,
      orgId,
      query: {
        direction: SortOrder.ASC,
        page,
        size,
        searchTerm: searchTerm || '',
        template: 'cati',
        subdone: 'false',
        skipdeletedcontacts: 'true',
      },
    })
  }, [orgId, token, searchTerm, page, size, getOrgSubs])

  const noData = React.useMemo(
    () => (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 20px',
          textAlign: 'center',
        }}
      >
        <Icon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }}>contacts</Icon>
        <Typography variant="h6" color="textSecondary">
          {searchTerm ? `No results for "${searchTerm}".` : 'No contacts were found.'}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          {searchTerm
            ? 'Try adjusting your search criteria.'
            : 'Subscribe contacts to a CATI campaign or check your access rights.'}
        </Typography>
      </Box>
    ),
    [searchTerm],
  )

  React.useEffect(() => {
    getSubs()
  }, [getSubs])

  return (
    <Loading isLoading={loading}>
      <ContentLayout>
        <TableToolbar>
          <SearchBar
            namespace="subs"
            tooltipTitle="Search contacts"
            searchTerm={searchTerm}
            onSubmit={(newSearchTerm: string) => {
              navigate({
                history,
                searchTerm: newSearchTerm,
                page,
              })
            }}
          />
          <Tooltip title="Reload contacts">
            <IconButton id="reload-subs" onClick={getSubs}>
              <Icon>refresh</Icon>
            </IconButton>
          </Tooltip>
          <ContactsSettingsMenu
            callContactMode={callContactMode}
            disabled={Boolean(loading || isCallContactModeSaving)}
            onCallContactModeChange={onCallContactModeChange}
          />
        </TableToolbar>

        <Box>
          {!values.length && noData}
          {!!values.length && (
            <>
              {values.map((contact) => (
                <SubCard key={contact.subId} callContactMode={callContactMode} contact={contact} timezone={timezone} />
              ))}

              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <TablePagination
                  namespace="Contacts"
                  page={page}
                  size={size}
                  total={total}
                  pageSizeValues={PAGE_SIZE_VALUES}
                  onPageSizeChange={(value) => {
                    setPageSize(value)
                    navigate({
                      history,
                      page: 1,
                      searchTerm: searchTerm || undefined,
                    })
                  }}
                  onPageChange={(newPage) => {
                    navigate({
                      history,
                      page: newPage,
                      searchTerm: searchTerm || undefined,
                    })
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </ContentLayout>
    </Loading>
  )
}

export default MobileSubs
